import React, { useState, useEffect } from 'react';

const AdmissionList = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedClass, setSelectedClass] = useState('');

  useEffect(() => {
    // Mock data for admission applications
    // In a real application, this would come from an API call
    const mockApplications = [
      {
        id: 1,
        studentName: 'John Smith',
        fatherName: 'Robert Smith',
        motherName: 'Emily Smith',
        email: 'john.smith@example.com',
        phone: '+1 (555) 123-4567',
        className: 'Grade 10',
        previousSchool: 'ABC High School',
        appliedDate: '2023-11-15',
        status: 'pending',
        documents: ['application_form.pdf', 'transcript.pdf', 'certificate.jpg']
      },
      {
        id: 2,
        studentName: 'Emma Johnson',
        fatherName: 'Michael Johnson',
        motherName: 'Sarah Johnson',
        email: 'emma.johnson@example.com',
        phone: '+1 (555) 234-5678',
        className: 'Grade 10',
        previousSchool: 'XYZ Secondary School',
        appliedDate: '2023-11-14',
        status: 'approved',
        documents: ['application_form.pdf', 'transcript.pdf']
      },
      {
        id: 3,
        studentName: 'Michael Brown',
        fatherName: 'William Brown',
        motherName: 'Olivia Brown',
        email: 'michael.brown@example.com',
        phone: '+1 (555) 345-6789',
        className: 'Grade 9',
        previousSchool: 'PQR Academy',
        appliedDate: '2023-11-13',
        status: 'rejected',
        documents: ['application_form.pdf', 'certificate.jpg']
      },
      {
        id: 4,
        studentName: 'Sarah Davis',
        fatherName: 'James Davis',
        motherName: 'Patricia Davis',
        email: 'sarah.davis@example.com',
        phone: '+1 (555) 456-7890',
        className: 'Grade 11',
        previousSchool: 'LMN High School',
        appliedDate: '2023-11-12',
        status: 'pending',
        documents: ['application_form.pdf', 'transcript.pdf', 'recommendation.pdf']
      },
      {
        id: 5,
        studentName: 'David Wilson',
        fatherName: 'Thomas Wilson',
        motherName: 'Jennifer Wilson',
        email: 'david.wilson@example.com',
        phone: '+1 (555) 567-8901',
        className: 'Grade 10',
        previousSchool: 'EFG Secondary School',
        appliedDate: '2023-11-11',
        status: 'approved',
        documents: ['application_form.pdf', 'transcript.pdf', 'certificate.jpg']
      }
    ];

    setTimeout(() => {
      setApplications(mockApplications);
      setLoading(false);
    }, 1000); // Simulate API call delay
  }, []);

  const filteredApplications = applications.filter(app => {
    return (!selectedStatus || app.status === selectedStatus) &&
           (!selectedClass || app.className === selectedClass);
  });

  const uniqueClasses = [...new Set(applications.map(app => app.className))];

  const updateStatus = (appId, newStatus) => {
    setApplications(applications.map(app => 
      app.id === appId ? { ...app, status: newStatus } : app
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Admission Applications</h1>
        
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 mb-2">Filter by Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Filter by Class</label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Classes</option>
                {uniqueClasses.map(className => (
                  <option key={className} value={className}>{className}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end">
              <button className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300 transition">
                Export
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Previous School</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applied Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documents</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredApplications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{app.studentName}</div>
                        <div className="text-sm text-gray-500">{app.email}</div>
                        <div className="text-sm text-gray-500">{app.phone}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>F: {app.fatherName}</div>
                      <div>M: {app.motherName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{app.className}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{app.previousSchool}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{app.appliedDate}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        app.status === 'approved' ? 'bg-green-100 text-green-800' :
                        app.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {app.documents.length} document{app.documents.length !== 1 ? 's' : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col space-y-2">
                        <button className="text-primary hover:text-blue-700 text-left">View Details</button>
                        {app.status === 'pending' && (
                          <>
                            <button 
                              onClick={() => updateStatus(app.id, 'approved')}
                              className="text-green-600 hover:text-green-800 text-left"
                            >
                              Approve
                            </button>
                            <button 
                              onClick={() => updateStatus(app.id, 'rejected')}
                              className="text-red-600 hover:text-red-800 text-left"
                            >
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Status Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Pending Applications</span>
                <span className="font-medium text-yellow-600">
                  {applications.filter(a => a.status === 'pending').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Approved Applications</span>
                <span className="font-medium text-green-600">
                  {applications.filter(a => a.status === 'approved').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Rejected Applications</span>
                <span className="font-medium text-red-600">
                  {applications.filter(a => a.status === 'rejected').length}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Class Distribution</h3>
            <div className="space-y-3">
              {uniqueClasses.map(className => (
                <div key={className} className="flex justify-between">
                  <span>{className}</span>
                  <span className="font-medium">
                    {applications.filter(a => a.className === className).length} applications
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span>Today's Applications</span>
                <span className="font-medium">
                  {applications.filter(a => a.appliedDate === new Date().toISOString().split('T')[0]).length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Last 7 Days</span>
                <span className="font-medium">
                  {applications.filter(a => {
                    const today = new Date();
                    const appliedDate = new Date(a.appliedDate);
                    const diffTime = Math.abs(today - appliedDate);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays <= 7;
                  }).length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Total Applications</span>
                <span className="font-medium">{applications.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionList;